import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { config } from '../../utils/config';
import BlogCard from './BlogCard';

const api = config.getFullApiUrl ? config.getFullApiUrl('/api/blogs') : `${config.API_URL}/api/blogs`;
const BLOG_MEDIA_PUBLIC_BASE = (process.env.REACT_APP_OBJECT_STORAGE_PUBLIC_BASE_URL || '').trim().replace(/\/+$/, '');
const DEFAULT_SKELETON_COUNT = 6;

const normalizeBlogImageUrl = (value) => {
  if (!value || typeof value !== 'string') return '';
  const raw = value.trim();
  if (!raw) return '';

  if (raw.startsWith('data:') || raw.startsWith('blob:')) {
    return raw;
  }

  if (raw.startsWith('//')) {
    return `https:${raw}`;
  }

  if (/^https?:\/\//i.test(raw)) {
    if (/X-Amz-Signature=|X-Amz-Credential=/i.test(raw)) {
      return raw;
    }
    if (BLOG_MEDIA_PUBLIC_BASE) {
      try {
        const parsed = new URL(raw);
        const isLegacyAwsS3 = /amazonaws\.com$/i.test(parsed.hostname);
        if (isLegacyAwsS3) {
          const marker = '/vardaanwebsites/';
          const markerIndex = parsed.pathname.toLowerCase().indexOf(marker);
          if (markerIndex >= 0) {
            const relativePath = parsed.pathname.slice(markerIndex + marker.length).replace(/^\/+/, '');
            if (relativePath) {
              return `${BLOG_MEDIA_PUBLIC_BASE}/${relativePath}`;
            }
          }
        }
      } catch (_) {
        return raw;
      }
    }
    return raw;
  }

  const apiBase = (config.API_URL || '').replace(/\/+$/, '');
  if (!apiBase) return raw;
  if (raw.startsWith('/')) return `${apiBase}${raw}`;
  return `${apiBase}/${raw.replace(/^\/+/, '')}`;
};

const fetchPublicBlogs = async () => {
  const res = await fetch(api);
  const text = await res.text();
  const contentType = res.headers.get('content-type') || '';

  if (!res.ok) {
    throw new Error(contentType.includes('application/json') ? 'Failed to load blogs' : 'Server unavailable');
  }

  if (!contentType.includes('application/json')) {
    throw new Error('Invalid response from blogs API');
  }

  const data = JSON.parse(text);
  if (!data.success || !Array.isArray(data.blogs)) {
    return [];
  }

  return data.blogs.map((blog) => ({
    ...blog,
    cover_image: normalizeBlogImageUrl(blog.cover_image),
  }));
};

function BlogListContainer({ onReadMore }) {
  const { data: blogs = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['publicBlogs'],
    queryFn: fetchPublicBlogs,
  });

  if (isLoading) {
    return (
      <div className="blog-loading" aria-busy="true" aria-live="polite">
        <div className="blog-grid-section">
          <div className="blog-grid blog-grid-skeletons">
            {Array.from({ length: DEFAULT_SKELETON_COUNT }).map((_, idx) => (
              <article key={`blog-skeleton-${idx}`} className="blog-card blog-card-skeleton" aria-hidden="true">
                <div className="blog-card-cover blog-skeleton-block blog-skeleton-shimmer" />
                <div className="blog-card-body">
                  <div className="blog-skeleton-title blog-skeleton-block blog-skeleton-shimmer" />
                  <div className="blog-skeleton-line blog-skeleton-block blog-skeleton-shimmer" />
                  <div className="blog-skeleton-line blog-skeleton-line-short blog-skeleton-block blog-skeleton-shimmer" />
                  <div className="blog-skeleton-chip blog-skeleton-block blog-skeleton-shimmer" />
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="blog-empty">
        <p>Failed to load blog posts.</p>
        <button type="button" onClick={() => refetch()}>Retry</button>
      </div>
    );
  }

  if (blogs.length === 0) {
    return (
      <div className="blog-empty">
        <p>No blog posts yet.</p>
      </div>
    );
  }

  const featuredBlog = blogs[0];
  const restBlogs = blogs.slice(1);

  return (
    <>
      {featuredBlog && <BlogCard blog={featuredBlog} featured onReadMore={onReadMore} />}

      {restBlogs.length > 0 && (
        <div className="blog-grid-section">
          <h3 className="blog-grid-heading">More articles</h3>
          <div className="blog-grid">
            {restBlogs.map((blog) => (
              <BlogCard key={blog.id} blog={blog} onReadMore={onReadMore} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default BlogListContainer;
